import React, { useEffect, useState } from "react";
import { X, Sparkles, Check, Loader2 } from "lucide-react";
import { api } from "../../api/client";

/**
 * Last-chance discounted-trial modal.
 *
 * Shown after a 2-second grace period when the user lands on the home page
 * with `?stripe_canceled=1&offer=full_trial&plan=monthly|yearly` in the URL,
 * i.e. they bailed out of a Stripe full-trial checkout.
 *
 * Visually it inherits the .pricing-modal-v2 neon style so it feels native.
 */
export default function DownsellTrialModal({ open, plan, onClose, isAuthed, onSignIn }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  if (!open) return null;

  const planLabel = plan === "yearly" ? "yearly" : "monthly";
  const regularPrice = plan === "yearly" ? "$383.99/yr" : "$39.99/mo";

  async function startDiscountedTrial() {
    if (!isAuthed) {
      onSignIn?.();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post("/stripe/checkout", {
        payment_type: "subscription",
        billing_period: planLabel,
        offer_type: "discounted_trial",
        origin_url: window.location.origin,
      });
      const url = res?.data?.url;
      if (!url) throw new Error("No checkout URL");
      window.location.href = url;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || String(e?.message || e);
      let msg = "Could not start checkout. " + detail;
      if (status === 403) msg = "This trial has already been used.";
      setError(msg);
      setBusy(false);
    }
  }

  return (
    <div className="pricing-overlay" onClick={onClose} data-testid="downsell-overlay">
      <div className="pricing-modal-v2 downsell-modal" onClick={(e) => e.stopPropagation()} data-testid="downsell-modal">
        <button className="pricing-close-btn" onClick={onClose} aria-label="Close" data-testid="downsell-close-btn">
          <X size={20} />
        </button>

        <div className="downsell-pill">
          <Sparkles size={12} /> Last-chance offer
        </div>

        <h1 className="pricing-title downsell-title">
          Wait! Try for <span className="downsell-bigprice">$1.99</span>
        </h1>
        <p className="downsell-subtitle">
          Get full access to AI Music Maker for 3 days for just <strong>$1.99</strong>.
          After the trial, your {planLabel} plan continues at <strong>{regularPrice}</strong> —
          cancel anytime in one click.
        </p>

        <ul className="pricing-features-list downsell-features">
          <li><Check className="pricing-check-icon" size={18} /><span><strong>100 credits</strong> for songs, videos, covers, clips</span></li>
          <li><Check className="pricing-check-icon" size={18} /><span><strong>Full song</strong> generation with AI vocals</span></li>
          <li><Check className="pricing-check-icon" size={18} /><span><strong>3-day money-back</strong> guarantee — cancel during trial, pay nothing more</span></li>
        </ul>

        {error && (
          <div className="pricing-checkout-error" role="alert" data-testid="downsell-error">
            <div className="pricing-checkout-error-body">{error}</div>
          </div>
        )}

        <div
          onClick={!busy ? startDiscountedTrial : undefined}
          className="pricing-trial-btn downsell-cta"
          style={{ cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}
          data-testid="downsell-cta-btn"
          role="button"
          tabIndex={0}
        >
          {busy ? (
            <span className="pricing-busy"><Loader2 size={16} className="pricing-spin" /> Redirecting to Stripe…</span>
          ) : (
            <>Start 3-Day Trial for <span style={{ fontWeight: 800 }}>$1.99</span></>
          )}
        </div>

        <button
          className="downsell-skip"
          onClick={onClose}
          data-testid="downsell-skip-btn"
        >
          No thanks, maybe later
        </button>

        <div className="pricing-secure-note">
          <Sparkles size={12} /> Secured by Stripe — your card is never stored on our servers.
        </div>
      </div>
    </div>
  );
}
